import { useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useRef, type Ref } from 'react';
import { api } from '../api';
import { formatPln } from '../catalog/money';
import type { AddGear } from '../quotes/AddPricedLine';
import { rentalsKey } from '../subrentals/types';
import { QuickPicker, type PickerDetail, type PickerOption } from '../ui/QuickPicker';
import { RowMenu } from '../ui/RowMenu';
import { markStyle, useContractorColors } from '../contractors/colors';
import { packlistKey, type PacklistView } from './types';

/**
 * As `GET /api/packlists/:eventId/suggest` sends it; `free` only for a role that may see availability, `suppliers`
 * (who has the item and how many, null: not known) only for a role that may see the suppliers' catalogs.
 */
type Suggestion = { type: 'item' | 'kit'; id: string; name: string; categoryName: string | null; free?: number; suppliers?: Array<{ supplierId?: string; name: string; quantity: number | null }> };
type Option = PickerOption & { suggestion: Suggestion };
/** A position of a supplier's catalog (`GET /api/quotes/events/:eventId/packlist-supplier-search`, as the quote's). */
type SupplierHit = { priceId: string; supplierId: string; supplierName: string; itemId: string | null; name: string; priceDayGrosze: number; quantity?: number | null };
/** Owner 2026-10-24 11:15 („tak samo jak w kosztorysie”): a supplier's own position, in the group „U dostawców”. */
type SupplierOption = PickerOption & { supplier: SupplierHit };
type Choice = Option | SupplierOption;

/** Owner 2026-10-24 16:19: in the supplier's colour, as in the quote's picker. */
const supplierOption = (h: SupplierHit, colors: ReadonlyMap<string, string>): SupplierOption => ({
  key: `sup-${h.priceId}`,
  name: h.name,
  details: [h.supplierName, `${formatPln(h.priceDayGrosze)} / doba`, ...(h.quantity != null ? [`ma ${h.quantity} szt.`] : [])],
  group: 'U dostawców',
  supplier: h,
  mark: markStyle(colors.get(h.supplierId)),
});

/**
 * Owner 2026-10-24 11:15, the quote's rules (#145): my gear is added as mine (a shortage goes through „Dopożycz”), so a
 * supplier's position of MY item is never offered by itself — when only the supplier's search found it, my item comes
 * under my name; only positions the supplier alone has are in „U dostawców”.
 */
export function withSuppliers<O extends { suggestion: Suggestion }>(mine: O[], suppliers: readonly SupplierHit[], mineOf: (s: Suggestion) => O, colors: ReadonlyMap<string, string> = new Map()): Array<O | SupplierOption> {
  const have = new Set(mine.filter((o) => o.suggestion.type === 'item').map((o) => o.suggestion.id));
  const extra: O[] = [];
  for (const h of suppliers) {
    if (h.itemId === null || have.has(h.itemId)) continue;
    have.add(h.itemId);
    extra.push(mineOf({ type: 'item', id: h.itemId, name: h.name, categoryName: null }));
  }
  return [...mine, ...extra, ...suppliers.filter((h) => h.itemId === null).map((h) => supplierOption(h, colors))];
}

/**
 * Owner 2026-10-23: the line under „wolne” — „u dostawców mają: A 4 · B 8 · łącznie z moimi wolnymi: 14”. „mają”, not
 * „wolne”: we do not know the suppliers' availability on the event's days. A supplier with an unknown quantity is named
 * without a number and the total gets a „+”. Nothing when no supplier has the item.
 */
export function supplierLine(free: number | undefined, suppliers: Suggestion['suppliers'], colors: ReadonlyMap<string, string> = new Map()): PickerDetail[] | undefined {
  if (!suppliers || suppliers.length === 0) return undefined;
  const known = suppliers.reduce((sum, s) => sum + (s.quantity ?? 0), 0);
  const plus = suppliers.some((s) => s.quantity === null) ? '+' : '';
  const total = free !== undefined ? `łącznie z moimi wolnymi: ${Math.max(0, free) + known}${plus}` : `łącznie: ${known}${plus}`;
  // owner 23:09 („bałagan”): each supplier in its own row, the total last
  // owner 23:09: each supplier's row with its colour stripe, as in the quote (grey without a colour)
  const row = (s: NonNullable<Suggestion['suppliers']>[number]): PickerDetail => ({
    text: s.quantity === null ? `${s.name} — ilość nieznana` : `${s.name} — ${s.quantity} szt.`,
    style: markStyle(s.supplierId ? colors.get(s.supplierId) : undefined),
  });
  return ['u dostawców mają:', ...suppliers.map(row), total];
}

const toOption = (s: Suggestion, colors: ReadonlyMap<string, string>): Option => ({
  key: `${s.type}-${s.id}`,
  name: s.name,
  details: [s.type === 'kit' ? 'zestaw' : (s.categoryName ?? 'bez kategorii'), ...(s.free !== undefined ? [`wolne: ${s.free}`] : [])],
  moreDetails: supplierLine(s.free, s.suppliers, colors),
  suggestion: s,
});

/**
 * Quick add to the packlist: the quick picker over catalog items and kits. An item always becomes a new line
 * (`onAdded` gets its id, so its quantity can be typed next); a kit goes into the lines of its items (`null`).
 */
export function QuickAdd({
  eventId,
  inputRef,
  onAdded,
  category,
  categoryId,
  inTable = false,
  gear,
}: {
  eventId: string;
  inputRef?: Ref<HTMLInputElement> | undefined;
  onAdded: (lineId: string | null) => void;
  /** The picker of a category's section in the editor (its label, e.g. "Nagłośnienie / Mikrofony"): that category's items first. */
  category?: string | undefined;
  /**
   * That category's id: the server puts its items (with subcategories, in the catalog's manual order) first, then the
   * items that have it in "Podpowiadaj też w kategoriach" (answer 5 of remarks 4), then the rest.
   */
  categoryId?: string | undefined;
  /** In the editor's table: the label is for screen readers, the placeholder says enough. */
  inTable?: boolean;
  /**
   * Owner 2026-10-24 11:15: the quote's ⚙ left of the field — „Wybierz z katalogu”, „Korzystaj z katalogów dostawców”
   * (for whoever works with suppliers as in the quote), „Wstaw blok lub szablon…”.
   */
  gear?: AddGear | undefined;
}) {
  const queryClient = useQueryClient();
  // read at search time (the colours are loaded long before anyone types)
  const colors = useRef<ReadonlyMap<string, string>>(new Map());
  colors.current = useContractorColors();

  const useSuppliers = !!gear?.canSuppliers && gear.useSuppliers;
  async function search(q: string, signal: AbortSignal): Promise<Choice[]> {
    const params = new URLSearchParams({ q });
    if (categoryId) params.set('categoryId', categoryId);
    const [res, suppliers] = await Promise.all([
      api<{ suggestions: Suggestion[] }>(`/api/packlists/${eventId}/suggest?${params.toString()}`, { signal }),
      useSuppliers ? api<{ hits: SupplierHit[] }>(`/api/quotes/events/${eventId}/packlist-supplier-search?${params.toString()}`, { signal }).then((r) => r.hits) : Promise.resolve([]),
    ]);
    const mine = res.suggestions.map((sg) => toOption(sg, colors.current));
    return withSuppliers(mine, suppliers, (sg) => ({ ...toOption(sg, colors.current), details: ['z mojego katalogu'] }), colors.current);
  }

  /** A supplier's position: „Odbiór: <dostawca>” and the event's rental of it (as from the quote). */
  async function addSupplier(h: SupplierHit, quantity: number) {
    await api(`/api/quotes/events/${eventId}/packlist-supplier-lines`, { method: 'POST', body: { priceId: h.priceId, quantity } });
    await Promise.all([queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }), queryClient.invalidateQueries({ queryKey: rentalsKey(eventId) })]);
    onAdded(null);
  }

  async function add(option: Option, quantity: number): Promise<boolean> {
    const { type, id } = option.suggestion;
    // gear another event reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24)
    const answer = await withConflictConfirm((confirmConflicts) =>
      api<PacklistView & { lineIds: string[] }>(`/api/packlists/${eventId}/lines`, {
        method: 'POST',
        body: { ...(type === 'kit' ? { kitId: id } : { itemId: id }), quantity, ...(confirmConflicts ? { confirmConflicts } : {}) },
      }),
    );
    if (!answer) return false;
    const { lineIds, ...view } = answer;
    queryClient.setQueryData(packlistKey(eventId), view);
    // the server names the line it created; a kit goes into the lines of its items (nothing to focus)
    onAdded(type === 'item' && lineIds.length === 1 ? lineIds[0]! : null);
    return true;
  }

  const picker = (
    <QuickPicker<Choice>
      label={category ? `Dodaj do kategorii: ${category}` : 'Dodaj do sprzętówki'}
      placeholder={useSuppliers ? 'Dodaj: też z katalogów dostawców' : category ? 'Dodaj pozycję, np. 4 sm58' : 'Dodaj sprzęt lub zestaw, np. qrp 40, 4x kabel xlr'}
      labelHidden={inTable}
      search={search}
      onPick={(o, quantity) => ('supplier' in o ? addSupplier(o.supplier, quantity) : add(o, quantity))}
      inputRef={inputRef}
      featureId="packlists.quick_add"
    />
  );
  if (!gear) return picker;
  return (
    <div className={`pl-add${useSuppliers ? ' pl-add-suppliers' : ''}`}>
      {/* as the quote's (#111): the field keeps the focus when the ⚙ is pressed (Safari does not focus buttons, #110) */}
      <span className="pl-add-gear" onMouseDown={(e) => e.preventDefault()} data-feature-id="packlists.suppliers">
        <RowMenu
          icon="gear"
          label="Więcej sposobów dodawania"
          floating
          actions={[
            { label: 'Wybierz z katalogu', onSelect: gear.onPickCatalog },
            ...(gear.canSuppliers ? [{ label: 'Korzystaj z katalogów dostawców', checked: gear.useSuppliers, onSelect: gear.onToggleSuppliers }] : []),
            ...(gear.onInsertBlock ? [{ label: 'Wstaw blok lub szablon…', onSelect: gear.onInsertBlock }] : []),
          ]}
        />
      </span>
      {picker}
    </div>
  );
}
